/**
 * The DOM half of "is there anything on screen a reload would throw away",
 * shared by the two automatic reloads that ask it: the version auto-update
 * (lib/freshness.ts, where these two checks were born and are explained) and
 * the recovery after an iPhone closes the Hub's database
 * (lib/firestoreRecovery.ts, packet B815). Moved here unchanged so both ask
 * the same question the same way rather than growing a second copy.
 *
 * The authoritative signals stay in lib/unsavedWork.ts (the registry and
 * the in-flight upload count); these are the backstop for the long tail.
 */

/**
 * The DOM half of unsaved-work detection: any non-empty <textarea> anywhere
 * in the document. A collapsed inbox row's reply box IS in the DOM (hidden,
 * not unmounted), which is why the draft store restores words only into an
 * expanded row (useComposerDraft's `enabled`): a hidden box full of restored
 * words would otherwise hold the auto-update off with nothing on screen.
 *
 * Textareas only - see the long note in unsavedWork.ts for why single-line
 * inputs are deliberately excluded.
 */
export function domHasUnsavedText(): boolean {
  try {
    const boxes = document.querySelectorAll('textarea');
    for (const box of boxes) {
      if (box.disabled || box.readOnly) continue;
      if (box.value.trim().length > 0) return true;
    }
    return false;
  } catch {
    // Treat an unexpected DOM failure as "might have work": the safe answer
    // is the pill, never a silent reload.
    return true;
  }
}

/**
 * An open modal means somebody is part-way through a decision. This leans on
 * a convention rather than a guarantee: the Hub's four role="dialog" surfaces
 * (AssistantChat, NavDrawer, FeedbackPrompt, and SubRequests' confirm sheet)
 * are all conditionally rendered inside {open && ...}, so being in the
 * document IS being open for them. A future always-mounted dialog styled
 * closed would read as permanently open here - which fails toward the pill,
 * the cheap direction - and a popover built without the dialog role is
 * invisible to this check, which is why the registry stays the authoritative
 * signal for anything that holds real work.
 */
export function modalIsOpen(): boolean {
  try {
    return !!document.querySelector('[role="dialog"], dialog[open]');
  } catch {
    return true;
  }
}
